
import React, { useState } from "react";
import Papa from "papaparse";
import { supabase } from "../supabaseClient";
import "../Components/UploadPage.css";
import logo from "../Components/Logo.png";

const UploadPage = () => {
  const [primusFile, setPrimusFile] = useState(null);
  const [carrierFile, setCarrierFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoaded, setComparisonLoaded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });

  const handlePrimusFileChange = (event) => setPrimusFile(event.target.files[0]);
  const handleCarrierFileChange = (event) => setCarrierFile(event.target.files[0]);

  const uploadPrimusDataToSupabase = async (data) => {
    const { data: insertedData, error } = await supabase.from("primus").upsert(data, { onConflict: ["tracking_ID"] });
    if (error) {
      console.error("Error uploading Primus data:", error.message || error);
      return false;
    }
    console.log("Primus data uploaded successfully:", insertedData);
    return true;
  };

  const uploadCarrierDataToSupabase = async (data) => {
    const { data: insertedData, error } = await supabase.from("carrier").upsert(data);
    if (error) {
      console.error("Error uploading Carrier data:", error.message || error);
      return false;
    }
    console.log("Carrier data uploaded successfully:", insertedData);
    return true;
  };

  const insertUniqueTrackingIDsIntoCarriertable = async () => {
    const { error } = await supabase.rpc("insert_unique_tracking_ids_into_carriertable");
    if (error) {
      console.error("Error calling Supabase function:", error.message || error);
      return false;
    }
    console.log("Unique TrackingIDs inserted into carriertable successfully");
    return true;
  };

  const sumRatesAndUpdateCarriertable = async () => {
    const { error } = await supabase.rpc("sum_rates_and_update_carriertable");
    if (error) {
      console.error("Error calling Supabase function:", error.message || error);
      return false;
    }
    console.log("Rates summed and updated in carriertable successfully");
    return true;
  };

  const clearDataFromSupabase = async () => {
    try {
      await supabase.from("primus").delete().neq("tracking_ID", "");
      await supabase.from("carrier").delete().neq("TrackingID", "");
      await supabase.from("carriertable").delete().neq("trackingid", "");
      await supabase.from("comparison").delete().neq("TrackingID", "");
      console.log("Data cleared from Supabase successfully");
    } catch (err) {
      console.error("Error clearing data from Supabase:", err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await clearDataFromSupabase();

      if (primusFile) {
        Papa.parse(primusFile, {
          complete: async (result) => {
            const primusMap = new Map();

            result.data.forEach((row, index) => {
              const trackingID = row.tracking_ID?.trim();
              if (!trackingID || !row.primus_Rate) return;

              let primusRate = parseFloat(row.primus_Rate);
              if (isNaN(primusRate)) {
                primusRate = 0;
                console.warn(`Invalid primus_Rate at index ${index}: ${row.primus_Rate}`);
              }

              primusMap.set(trackingID, {
                tracking_ID: trackingID,
                BOL: row.BOL,
                primus_Rate: primusRate,
                vendor: row.vendor,
              });
            });

            const primusData = Array.from(primusMap.values());
            const primusUploadSuccess = await uploadPrimusDataToSupabase(primusData);
            if (!primusUploadSuccess) return setLoading(false);

            if (carrierFile) {
              Papa.parse(carrierFile, {
                complete: async (result) => {
                  const carrierData = result.data.map((row) => ({
                    TrackingID: row.TrackingID,
                    price: row.price,
                    description: row.description,
                  }));

                  const carrierUploadSuccess = await uploadCarrierDataToSupabase(carrierData);
                  if (!carrierUploadSuccess) return setLoading(false);

                  if (!(await insertUniqueTrackingIDsIntoCarriertable())) return setLoading(false);
                  if (!(await sumRatesAndUpdateCarriertable())) return setLoading(false);

                  setLoading(false);
                  alert("Primus and Carrier data uploaded and processed successfully!");
                },
                header: true,
              });
            } else {
              alert("Carrier file is missing!");
              setLoading(false);
            }
          },
          header: true,
        });
      } else {
        alert("Primus file is missing!");
        setLoading(false);
      }
    } catch (err) {
      console.error("Error during processing:", err);
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csv = Papa.unparse(comparisonData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Primus_Comparison_Data.csv";
    link.click();
  };

  const startComparison = async () => {
    try {
      const { error } = await supabase.rpc("populate_comparison_table");
      if (error) return console.error("Error populating comparison table:", error.message);

      let allData = [];
      let from = 0;
      const batchSize = 1000;
      let done = false;

      while (!done) {
        const { data, error } = await supabase.from("comparison").select("*").range(from, from + batchSize - 1);
        if (error) return console.error("Error fetching comparison data:", error.message);

        if (data.length === 0) {
          done = true;
        } else {
          allData = [...allData, ...data];
          from += batchSize;
          if (data.length < batchSize) done = true;
        }
      }

      setComparisonData(allData);
      setComparisonLoaded(true);
      alert(`Comparison table populated with ${allData.length} rows successfully!`);
    } catch (err) {
      console.error("Error during comparison:", err);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const downloadPrimusTemplate = () => {
    const templateData = [{ BOL: "", tracking_ID: "", primus_Rate: "", vendor: "" }];
    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Primus_Template.csv";
    link.click();
  };

  const downloadCarrierTemplate = () => {
    const templateData = [{ TrackingID: "", price: "", description: "" }];
    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Carrier_Template.csv";
    link.click();
  };

  const sortedData = React.useMemo(() => {
    const sortableItems = [...comparisonData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "ascending" ? aValue - bValue : bValue - aValue;
        } else {
          return sortConfig.direction === "ascending"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    }
    return sortableItems;
  }, [comparisonData, sortConfig]);

  return (
    <div className="upload-page">
      <div className="header">
        <img src={logo} alt="Logo" />
      </div>

      <div>
        <h3>Upload Primus Data (CSV)</h3>
        <input type="file" accept=".csv" onChange={handlePrimusFileChange} />
      </div>

      <div>
        <h3>Upload Carrier Data (CSV)</h3>
        <input type="file" accept=".csv" onChange={handleCarrierFileChange} />
      </div>

      <div className="button-container">
        <button onClick={downloadPrimusTemplate}>Primus Template</button>
        <button onClick={downloadCarrierTemplate}>Carrier Template</button>
        <button onClick={handleSubmit} disabled={loading}>{loading ? "Uploading..." : "Process"}</button>
        <button onClick={startComparison} disabled={loading}>Start Comparison</button>
        <button onClick={exportToCSV} disabled={loading}>Export to CSV</button>
      </div>

      {comparisonLoaded && comparisonData.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort("TrackingID")}>TrackingID</th>
                <th onClick={() => requestSort("Primus_Rate")}>Primus Rate</th>
                <th onClick={() => requestSort("Carrier_Rate")}>Carrier Rate</th>
                <th onClick={() => requestSort("BOL")}>BOL</th>
                <th onClick={() => requestSort("status")}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, index) => (
                <tr key={index}>
                  <td>{row.TrackingID}</td>
                  <td>{row.Primus_Rate}</td>
                  <td>{row.Carrier_Rate}</td>
                  <td>{row.BOL}</td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
